import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  ArrowLeftRight, CalendarDays, ChevronRight, LayoutList, MapPin, Search, Sparkles,
  Ticket, TrendingUp,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { Drawer, Select, apiMutate, usePermissions } from '../admin/primitives.js'
import { promotedEventIds, projectHistory } from './history-projection.js'
import { ReorderableSection, useSectionOrder } from '../layout/SectionOrder.js'
import { setUserPref, usePrefsStore } from '../prefs.js'
import {
  AIDE_SCANS, FreshnessLine, PhasePill, SOURCE_LABELS, honnete,
  jourFr, plageHoraire, LiveNumber, type SoireeView, type TicketingFreshness,
} from './lib.js'
import { LigneCaisseNuit, useCaisseNuit, useCaisseParMois, useDemandeQuandVisible } from './CaisseNuit.js'
import { useMenuSoiree } from './menu-soiree.js'
import { dateParis, plageDesDates, plageDuMois, type ReponseCaisse } from './caisse-nuit-model.js'
import { NuitBilletteries } from './NuitBilletteries.js'
import { afficherLaNuit, grandNombre, type AffichageBilletteries, type VueNuit } from './nuit-billetteries-model.js'
import { BandeauARattacher, NomsAssociations, SousLigneRattachees } from './Rattachements.js'
import { nombreAffiche, prefixeSoireeDe, type ChampsRattachement } from './rattachement-model.js'
import { ligneBarCourte, type VenueFormules } from './formules-model.js'
import { BoutonNouvelleSoiree, EncartBilletterieMaison, FenetreNouvelleSoiree, useBilletterieMaison } from '../billetterie/EncartBilletterieMaison.js'

/**
 * /soirees — PAGE DE PILOTAGE (v3).
 *
 * Hiérarchie : soirée EN COURS (compteur dominant) → PROCHAINES SOIRÉES
 * (préventes, prévision, concurrence) → AUTOUR DE RENNES (veille) →
 * HISTORIQUE (périodes configurables, groupé par saison) → Comparer.
 * Pas de scroll infini : l'historique est plié par saison et par mois.
 */

interface Season { name: string; start: string; end: string; boundary_source: string; events_count?: number }
interface IndexData {
  freshness: TicketingFreshness
  seasons: Season[]
  period: string
  current: SoireeView | null
  /** La nuit en cours, toutes billetteries (16/10/2026 ; absente d'un serveur plus ancien). */
  night?: VueNuit | null
  billetteries?: AffichageBilletteries
  /** « Formules pour le bar » de la soirée en cours (19/10/2026) ; `null` sans billet d'association ou réglage éteint. */
  current_formules?: VenueFormules | null
  freshness_by_billetterie?: Array<TicketingFreshness & { billetterie: string; cle: string }>
  upcoming: SoireeView[]
  history: SoireeView[]
  history_total: number
}
interface ForecastView {
  available: boolean
  reason?: string
  range_low: number | null
  range_high: number | null
  confidence: string
  reference_quality?: 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW'
  family_refs_count?: number
  market_coverage?: string | null
  /** Préventes AU JALON de comparaison — pas l'état courant. */
  presales_at_milestone: number | null
  milestone: string | null
  presales_vs_ref_pct: number | null
  pacing: string | null
  market_competition: string | null
  factors: { kind: string; label: string; detail: string }[]
  comparables: { id: string; name: string; starts_at: string; final_scans: number
    weight?: number; family_match?: boolean; atypical?: boolean }[]
}
interface PressureWarning {
  venue_name: string; competition_class: string; title: string
  starts_at: string; reasons: string[]; level: 'HIGH' | 'MEDIUM'; source_url: string | null
}
interface OutlookEntry {
  soiree: SoireeView
  forecast: ForecastView
  presales: Record<string, number | null>
  competition: number
  pressure?: { level: string; level_label: string; signals_total: number; signals_high_impact: number
    factors: { label: string; detail: string; points: number }[]
    warnings?: PressureWarning[]
    coverage?: string; coverage_detail?: { direct_total: number; direct_monitored: number } } | null
  forecast_delta_since_yesterday?: number | null
  /**
   * Les billets des associations rattachées (19/10/2026), à côté des préventes
   * Shotgun. Jamais d'étiquette « Soirée association » avant la nuit : la
   * prévision Shotgun reste (L'ÉTUDIANTE vend presque tout le jour même).
   */
  associations_valid?: number | null
}
interface MarketEvent {
  id: string; title: string; starts_at: string; venue_name: string | null; category: string | null
  distance_km: number | null; relevance_level: 'FORT' | 'MOYEN' | 'FAIBLE'
  sources: { name: string; url: string }[]
  last_verified_at: string
}
interface HomeSignal {
  id: string; venue_id: string | null; venue_name: string | null; type_label: string; summary: string
  event_start_at: string | null; impact: 'HIGH' | 'MEDIUM' | 'LOW'
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'; last_seen_at: string
}
interface HomeVenue { id: string; competition_class: string }
interface WeekRetrospective {
  /* Une seule section, deux projections : les soirées déjà terminées de la
     semaine en cours dès qu'il y en a, sinon le dernier week-end exploité. */
  kind: 'CURRENT_WEEK' | 'LAST_WEEKEND'
  label: string; from: string; to: string
  events: Array<{ id: string; name: string; starts_at: string; day: string; phase: string
    family: string | null; tickets_valid: number | null; tickets_scanned: number | null } & ChampsRattachement>
}
const TITRE_RETRO: Record<WeekRetrospective['kind'], string> = {
  CURRENT_WEEK: 'Cette semaine — soirées passées',
  LAST_WEEKEND: 'Dernier week-end',
}

const PERIOD_OPTIONS = [
  { value: 'last-month', label: 'Dernier mois' },
  { value: '3-months', label: '3 derniers mois' },
  { value: '6-months', label: '6 derniers mois' },
  { value: 'current-season', label: 'Saison actuelle' },
  { value: 'previous-season', label: 'Saison précédente' },
  { value: 'all', label: 'Toutes les saisons' },
  { value: 'custom', label: 'Période personnalisée' },
]

async function fetchJson<T>(url: string): Promise<T | null> {
  try { const r = await fetch(url, { credentials: 'same-origin' }); return r.ok ? (await r.json()) as T : null }
  catch { return null }
}

/* ═══ carte héroïque — LE CHIFFRE DOMINE (règle produit) ═══ */
function CurrentCard({ s, freshness, nuit, billetteries, formules }: {
  s: SoireeView; freshness: TicketingFreshness; nuit?: VueNuit | null | undefined; billetteries?: AffichageBilletteries | undefined
  formules?: VenueFormules | null | undefined
}) {
  /* LA NUIT D'ABORD (réglage par défaut) : le 15/10, la carte disait 421 ; la
     porte avait vu 636 entrées, dont 215 vendues sur Pretix. Le grand nombre
     est alors celui de la nuit, et les lignes disent d'où il vient. */
  const grand = grandNombre(s.tickets_scanned, nuit, billetteries, 'soirees')
  const montrerNuit = afficherLaNuit(nuit, billetteries, 'soirees')
  const menuSoiree = useMenuSoiree()
  return (
    <Link href={`/soirees/${s.id}`} className="so-hero lc-card lc-card--violet" {...menuSoiree.soiree(s)}>
      <span className="lc-card__edge" aria-hidden="true" />
      <div className="so-hero__head">
        <div className="so-hero__title">
          <h2>{s.name}</h2>
          <p className="so-hero__when">
            <CalendarDays size={15} aria-hidden="true" />
            {jourFr(s.starts_at)} · {plageHoraire(s.starts_at, s.ends_at)}
          </p>
          {/* Un événement rattaché a ouvert la porte avant elle : la nuit est en cours. */}
          {s.ouverte_avant ? <p className="so-hero__when">{s.ouverte_avant.libelle}</p> : null}
        </div>
        <PhasePill phase={s.phase} label={s.phase_label} />
      </div>
      <div className="so-hero__count">
        {/* Le traitement du nombre vient de la primitive partagée : cette
            page reste la référence visuelle, elle ne la redéfinit plus. */}
        <strong className="lc-bignumber"><LiveNumber value={grand.valeur} /></strong>
        <span className="so-hero__count-label">{grand.libelle}</span>
      </div>
      {montrerNuit && nuit ? <NuitBilletteries nuit={nuit} sansTete={grand.estLaNuit} /> : null}
      {/* Le bar en direct : combien de chaque formule sont déjà entrés (billets d'associations). */}
      {formules ? <p className="so-hero__bar"><strong>Bar</strong> · {ligneBarCourte(formules.formules)}</p> : null}
      <FreshnessLine f={freshness} />
      <span className="so-hero__cta">Ouvrir la soirée <ChevronRight size={16} aria-hidden="true" /></span>
    </Link>
  )
}

/* ═══ RÉTROSPECTIVE — même section, deux projections (Phase 2 §38-41) ═══
   Les CARTES ne changent pas : une soirée passée de cette semaine se lit
   exactement comme une soirée du dernier week-end. Seul le titre dit
   laquelle des deux questions la page est en train de répondre. */
function RetrospectiveHero({ retro, caisse }: { retro: WeekRetrospective; caisse: ReponseCaisse | null }) {
  const menuSoiree = useMenuSoiree()
  return (
    <section aria-labelledby="so-weekend">
      <div className="so-section-head">
        <h2 className="lc-section-title" id="so-weekend">
          <CalendarDays size={16} aria-hidden="true" /> {TITRE_RETRO[retro.kind]}
        </h2>
        <span className="lc-hint">{retro.label}</span>
      </div>
      <div className="so-weekend">
        {retro.events.map((e) => (
          <Link key={e.id} href={`/soirees/${e.id}`} className="so-weekend__card lc-card lc-card--violet" {...menuSoiree.soiree(e, caisse)}>
            <span className="lc-card__edge" aria-hidden="true" />
            <span className="so-weekend__name">{e.name}</span>
            <span className="so-weekend__when">{jourFr(e.starts_at)}</span>
            <span className="so-weekend__nums">
              <strong>{nombreAffiche(e) ?? '—'}</strong>
              <span>scannés{(e.total_valid ?? e.tickets_valid) !== null ? ` · ${e.total_valid ?? e.tickets_valid} vendus` : ''}</span>
            </span>
            {/* Une carte est un lien : la sous-ligne y est en texte seul, « Défaire » vit sur la fiche. */}
            {(e.rattachees_resume ?? []).length > 0 ? <SousLigneRattachees resume={e.rattachees_resume} defaire={false} /> : null}
            {e.family ? <span className="so-weekend__family">{e.family}</span> : null}
            {/* La caisse de la NUIT, en second plan : jamais à la taille des billets. */}
            <LigneCaisseNuit r={caisse} soireeId={e.id} />
            <span className="so-hero__cta">Ouvrir la soirée <ChevronRight size={14} aria-hidden="true" /></span>
          </Link>
        ))}
      </div>
    </section>
  )
}

/* ═══ prochaine soirée : préventes + prévision + concurrence ═══ */
function OutlookCard({ entry, onWhy }: { entry: OutlookEntry; onWhy: () => void }) {
  const { soiree: s, forecast: f } = entry
  const menuSoiree = useMenuSoiree()
  return (
    <article className="so-outlook lc-card lc-card--cyan">
      <span className="lc-card__edge" aria-hidden="true" />
      <header className="so-outlook__head">
        <Link href={`/soirees/${s.id}`} className="so-outlook__name" {...menuSoiree.soiree(s)}>{s.name}</Link>
        <span className="so-outlook__date">{jourFr(s.starts_at)}</span>
      </header>
      <div className="so-outlook__grid">
        {/*
          PRÉVENTES = L'ÉTAT COURANT, PAS UN JALON.

          Cette tuile lisait `presales_now` du calcul de prévision — un
          nombre coupé à « départ − offset du jalon franchi », fait pour être
          comparable aux références au MÊME repère. Le jeudi 27/08, elle
          affichait donc « 0 préventes à J-3 » alors que dix billets étaient
          vendus : trois jours plus tôt, il n'y en avait effectivement aucun.

          Deux nombres, deux natures, deux sources :
            · combien de billets sont vendus MAINTENANT → tickets_valid ;
            · dans combien de nuits a lieu la soirée    → countdown_label.
          Le jalon de prévision reste dans le détail, là où il explique le
          rythme — sa place légitime.
        */}
        <span className="so-minitile">
          <strong>{honnete(s.tickets_valid)}</strong>
          <span>préventes{entry.associations_valid ? ' Shotgun' : ''}{s.countdown_label ? ` · ${s.countdown_label}` : ''}</span>
        </span>
        {/* Les associations rattachées : dites à côté, sans toucher à la prévision Shotgun. */}
        {entry.associations_valid ? (
          <span className="so-minitile">
            <strong>{entry.associations_valid.toLocaleString('fr-FR')}</strong>
            <span>billets d’associations</span>
          </span>
        ) : null}
        {f.presales_vs_ref_pct !== null ? (
          <span className="so-minitile">
            <strong className={f.presales_vs_ref_pct >= 0 ? 'so-up' : 'so-down'}>
              {f.presales_vs_ref_pct >= 0 ? '+' : ''}{f.presales_vs_ref_pct.toLocaleString('fr-FR')} %
            </strong>
            <span>vs référence</span>
          </span>
        ) : null}
        {f.available && f.range_low !== null ? (
          <button type="button" className="so-minitile so-clickable" onClick={onWhy}
            aria-label="Pourquoi cette prévision ?">
            <strong>{f.range_low}–{f.range_high}</strong>
            <span>projection scans · confiance {f.confidence.toLowerCase()}</span>
          </button>
        ) : (
          <span className="so-minitile"><strong>—</strong><span>pas de prévision fiable</span></span>
        )}
        {f.pacing ? <span className="so-minitile"><strong>{f.pacing === 'ACCÉLÈRE' ? '↗' : f.pacing === 'RALENTIT' ? '↘' : '→'}</strong><span>rythme : {f.pacing.toLowerCase()}</span></span> : null}
        {entry.forecast_delta_since_yesterday != null && entry.forecast_delta_since_yesterday !== 0 ? (
          <span className="so-minitile">
            <strong className={entry.forecast_delta_since_yesterday > 0 ? 'so-up' : 'so-down'}>
              {entry.forecast_delta_since_yesterday > 0 ? '+' : ''}{entry.forecast_delta_since_yesterday}
            </strong>
            <span>depuis hier</span>
          </span>
        ) : null}
        {entry.pressure && entry.pressure.level !== 'LOW' ? (
          <span className={`so-minitile so-minitile--warn${entry.pressure.level === 'VERY_HIGH' ? ' so-minitile--alert' : ''}`}>
            <strong>{entry.pressure.level_label}</strong>
            <span>concurrence · {entry.pressure.signals_total} signal{entry.pressure.signals_total > 1 ? 'aux' : ''}</span>
          </span>
        ) : entry.competition > 0 ? (
          <span className="so-minitile so-minitile--warn"><strong>{entry.competition}</strong><span>concurrent{entry.competition > 1 ? 's' : ''} détecté{entry.competition > 1 ? 's' : ''}</span></span>
        ) : null}
      </div>
      {entry.pressure && entry.pressure.factors.length > 0 && entry.pressure.level !== 'LOW' ? (
        <p className="lc-hint so-outlook__pressure">
          {entry.pressure.factors.slice(0, 2).map((x) => x.label).join(' · ')}
          {' — '}<Link href="/soirees/market">voir la veille</Link>
        </p>
      ) : null}
      {(entry.pressure?.warnings ?? []).slice(0, 2).map((w, i) => (
        <p key={i} className="so-outlook__warning">
          ⚠ Événement majeur à proximité : <strong>{w.venue_name}</strong> — {w.title}
          {w.reasons.length > 0 ? ` (${w.reasons.join(', ')})` : ''}
        </p>
      ))}
      <Link href={`/soirees/${s.id}`} className="so-hero__cta" {...menuSoiree.soiree(s)}>Voir la soirée <ChevronRight size={14} aria-hidden="true" /></Link>
    </article>
  )
}

/* ═══ veille — Autour de Rennes (v4 : signaux commerciaux D'ABORD) ═══ */
function MarketBlock() {
  const [events, setEvents] = useState<MarketEvent[] | null>(null)
  const [signals, setSignals] = useState<HomeSignal[]>([])
  const [classOf, setClassOf] = useState<Map<string, string>>(new Map())
  useEffect(() => {
    let vivant = true
    void fetchJson<{ events: MarketEvent[]; signals: HomeSignal[]; venues: HomeVenue[] }>('/api/v1/soirees/market')
      .then((j) => {
        if (!vivant) return
        setEvents(j?.events ?? [])
        setSignals(j?.signals ?? [])
        setClassOf(new Map((j?.venues ?? []).map((v) => [v.id, v.competition_class])))
      })
    return () => { vivant = false }
  }, [])
  if (events === null) return null
  // §40 : la CONCURRENCE DIRECTE d'abord (boîtes/nightlife), les événements
  // contextuels (salles de concert…) à part — jamais mélangés.
  const isDirect = (s: HomeSignal) => ['DIRECT', 'ADJACENT'].includes(s.venue_id ? classOf.get(s.venue_id) ?? 'DIRECT' : 'DIRECT')
  const direct = signals.filter((s) => s.impact !== 'LOW' && isDirect(s)).slice(0, 4)
  const contextual = signals.filter((s) => s.impact !== 'LOW' && !isDirect(s)).slice(0, 3)
  const relevant = events.filter((e) => e.relevance_level !== 'FAIBLE').slice(0, Math.max(1, 4 - contextual.length))
  const SignalCardHome = ({ s }: { s: HomeSignal }) => (
    <Link href="/soirees/market" className={`so-sig so-sig--${s.impact.toLowerCase()}`}>
      <span className="so-sig__badge">{s.type_label}</span>
      <span className="so-sig__body">
        <span className="so-sig__venue">{s.venue_name ?? 'Établissement inconnu'}</span>
        <span className="so-sig__summary">{s.summary}</span>
        <span className="lc-hint so-sig__meta">
          {s.event_start_at ? `${jourFr(s.event_start_at)} · ` : 'date à confirmer · '}
          confiance {s.confidence === 'HIGH' ? 'élevée' : s.confidence === 'MEDIUM' ? 'moyenne' : 'faible'}
        </span>
      </span>
    </Link>
  )
  return (
    <section aria-labelledby="so-market">
      <div className="so-section-head">
        <h2 className="lc-section-title" id="so-market"><MapPin size={16} aria-hidden="true" /> Autour de Rennes</h2>
        <Link href="/soirees/market" className="lc-hint">Voir toute la veille</Link>
      </div>
      {direct.length === 0 && contextual.length === 0 && relevant.length === 0 ? (
        <p className="gd-calm">Aucun signal concurrent pertinent détecté — la veille reste une donnée externe, jamais une certitude.</p>
      ) : (
        <>
          {direct.length > 0 ? (
            <>
              <h3 className="so-class-title">Concurrence directe</h3>
              <div className="so-sigs so-sigs--home">
                {direct.map((s) => <SignalCardHome key={s.id} s={s} />)}
              </div>
            </>
          ) : null}
          {contextual.length > 0 || relevant.length > 0 ? (
            <h3 className="so-class-title">Autres événements à surveiller</h3>
          ) : null}
          {contextual.length > 0 ? (
            <div className="so-sigs so-sigs--home">
              {contextual.map((s) => <SignalCardHome key={s.id} s={s} />)}
            </div>
          ) : null}
          {relevant.length > 0 ? (
            <div className="lc-panel gd-panel"><div className="lc-panel__body lc-panel__body--flush so-rows">
              {relevant.map((e) => (
                <a key={e.id} href={e.sources[0]?.url} target="_blank" rel="noopener noreferrer" className="so-row">
                  <span className={`so-mkt-level so-mkt-level--${e.relevance_level.toLowerCase()}`}>{e.relevance_level}</span>
                  <span className="so-row__body">
                    <span className="so-row__name">{e.title}</span>
                    <span className="so-row__when">
                      {jourFr(e.starts_at)} · {e.venue_name ?? 'lieu inconnu'}
                      {e.category ? ` · ${e.category}` : ''}
                      {e.distance_km !== null ? ` · ${e.distance_km.toLocaleString('fr-FR')} km` : ''}
                      {' · source : '}{e.sources[0]?.name ?? '—'}
                    </span>
                  </span>
                  <ChevronRight size={16} className="so-row__chev" aria-hidden="true" />
                </a>
              ))}
            </div></div>
          ) : null}
        </>
      )}
    </section>
  )
}

/* ═══ un mois de l'historique : sa caisse se lit quand il paraît ═══ */
function MoisHistorique({ month, list, caisse, onChange }: {
  month: string; list: SoireeView[]; caisse: ReturnType<typeof useCaisseParMois>; onChange: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Le mois civil ENTIER (pas les dates des lignes) : une recherche qui filtre retombe sur la même lecture.
  const plage = list[0] ? plageDuMois(dateParis(list[0].starts_at)) : null
  useDemandeQuandVisible(ref, () => caisse.demander(plage))
  const r = caisse.reponse(plage)
  return (
    <div ref={ref} className="so-month">
      <h3 className="so-month__name">{month.charAt(0).toUpperCase() + month.slice(1)}</h3>
      <div className="lc-panel gd-panel"><div className="lc-panel__body lc-panel__body--flush so-rows">
        {list.map((s) => <SoireeRowItem key={s.id} s={s} caisse={r} onChange={onChange} />)}
      </div></div>
    </div>
  )
}

/* ═══ ligne d'historique ═══ */
/* Une soirée = UNE ligne par nuit (19/10/2026) : ses événements d'associations
   rattachés sont dans son nombre et dans la sous-ligne « + MEDECINE (Pretix) ·
   rattachée automatiquement · Défaire ». La sous-ligne vit HORS du lien — un
   bouton dans un lien n'est ni valide ni prévisible au clic. */
function SoireeRowItem({ s, caisse, onChange }: { s: SoireeView; caisse: ReponseCaisse | null; onChange: () => void }) {
  const menuSoiree = useMenuSoiree()
  const n = nombreAffiche(s)
  const v = s.total_valid ?? s.tickets_valid
  const resume = s.rattachees_resume ?? []
  const ligne = (
    <Link href={`/soirees/${s.id}`} className={`so-row${s.rattachee_a ? ' so-row--rattachee' : ''}`} {...menuSoiree.soiree(s, caisse)}>
      <span className="so-row__icon" aria-hidden="true"><Sparkles size={17} /></span>
      <span className="so-row__body">
        <span className="so-row__name">{s.name}</span>
        {s.groupe ? <span className="so-row__when">{s.groupe.nombre} événements d’associations regroupés · {SOURCE_LABELS[s.source] ?? s.source}</span> : null}
        {s.rattachee_a ? <span className="so-row__when">Rattachée à « {s.rattachee_a.name} »</span> : null}
        <span className="so-row__when">{jourFr(s.starts_at)} · {plageHoraire(s.starts_at, s.ends_at)}</span>
        <LigneCaisseNuit r={caisse} soireeId={s.id} />
      </span>
      {n !== null ? (
        <span className="so-row__scans"><strong>{n}</strong><span>scannés{v ? ` / ${v}` : ''}</span></span>
      ) : null}
      <PhasePill phase={s.phase} label={s.phase_label} />
      <ChevronRight size={16} className="so-row__chev" aria-hidden="true" />
    </Link>
  )
  // La soirée d'une association (20/10/2026) : « Soirée de Médecine », lien HORS de la ligne (elle est déjà un lien).
  const asso = s.association ?? null
  if (resume.length === 0 && !asso) return ligne
  return (
    <div className="so-row-groupe">
      {ligne}
      {asso ? <div className="so-ratt-sous"><span className="so-ratt-sous__ligne">{prefixeSoireeDe(asso.name)}<NomsAssociations associations={[asso]} prefixe="" /></span></div> : null}
      <SousLigneRattachees resume={resume} onChange={onChange} />
    </div>
  )
}

/** `nouvelle` : arrivée par `/soirees/nouvelle` — la page, avec la fenêtre « Nouvelle soirée » ouverte par-dessus. */
export function SoireesPage({ nouvelle = false }: { nouvelle?: boolean } = {}) {
  const meta = useShellMeta()
  const permissions = usePermissions()
  const menuSoiree = useMenuSoiree()
  const prefs = usePrefsStore()
  const [data, setData] = useState<IndexData | null>(null)
  const [outlook, setOutlook] = useState<OutlookEntry[] | null>(null)
  const [period, setPeriod] = useState(prefs.resolved['soirees_period'] ?? 'current-season')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [recherche, setRecherche] = useState('')
  const [whyEntry, setWhyEntry] = useState<OutlookEntry | null>(null)
  const [retro, setRetro] = useState<WeekRetrospective | null>(null)
  // Billetterie Live Club : sa propre lecture, ses propres erreurs — seul l'encart les dit.
  const billetterieMaison = useBilletterieMaison(nouvelle)

  /*
   * ORDRE DES SECTIONS — un défaut GLOBAL, pas une préférence personnelle.
   *
   * Même doctrine que la page Soirée : « les prochaines soirées avant le
   * dernier week-end » n'est pas un goût, c'est une décision sur ce que la
   * page est. La liste passée ici sert d'ordre de repli quand le serveur
   * n'est pas joignable ; sinon c'est lui qui décide, registre canonique
   * compris.
   */
  const ordre = useSectionOrder('soirees-list', [
    { id: 'current', label: 'Soirée en cours' },
    { id: 'outlook', label: 'Prochaines soirées' },
    /* L'IDENTIFIANT NE BOUGE PAS.
       `last-weekend` nomme un EMPLACEMENT dans la page, pas son contenu du
       jour. Le renommer aurait invalidé toutes les dispositions déjà
       enregistrées, pour un gain nul : c'est le même slot fonctionnel. */
    { id: 'last-weekend', label: TITRE_RETRO[retro?.kind ?? 'LAST_WEEKEND'] },
    { id: 'market', label: 'Autour de Rennes' },
    { id: 'history', label: 'Historique' },
  ])

  // Un rattachement défait (ou décidé) se voit tout de suite, sans attendre les 30 s.
  const [relire, setRelire] = useState(0)
  const recharger = () => setRelire((x) => x + 1)
  useEffect(() => {
    let vivant = true
    const params = new URLSearchParams({ period })
    if (period === 'custom') {
      if (customFrom) params.set('from', customFrom)
      if (customTo) params.set('to', customTo)
    }
    const charger = () => {
      void fetchJson<IndexData>(`/api/v1/soirees?${params}`)
        .then((j) => { if (vivant && j) setData(j) })
    }
    charger()
    const t = setInterval(charger, 30_000)
    return () => { vivant = false; clearInterval(t) }
  }, [period, customFrom, customTo, relire])

  useEffect(() => {
    let vivant = true
    void fetchJson<{ retrospective: WeekRetrospective | null }>('/api/v1/soirees/week-retrospective')
      .then((j) => { if (vivant) setRetro(j?.retrospective ?? null) })
    return () => { vivant = false }
  }, [])

  useEffect(() => {
    let vivant = true
    const charger = () => {
      void fetchJson<{ upcoming: OutlookEntry[] }>('/api/v1/soirees/outlook')
        .then((j) => { if (vivant) setOutlook(j?.upcoming ?? []) })
    }
    charger()
    const t = setInterval(charger, 120_000)
    return () => { vivant = false; clearInterval(t) }
  }, [])

  const changePeriod = (p: string) => {
    setPeriod(p)
    void setUserPref({ soirees_period: p }, (m, pth, b2) => apiMutate(m, pth, b2))
  }

  const q = recherche.trim().toLowerCase()

  /* LA CAISSE DE LA NUIT — les cartes de la semaine : une lecture pour la
     semaine. La plage ne change pas au rafraîchissement de 30 s : aucune
     relecture.

     L'historique, lui, lit MOIS PAR MOIS, quand un mois paraît à l'écran
     (`MoisHistorique`) : chaque nuit avec une trace de caisse coûte au
     serveur la fiche de nuit entière, et la période affichée va jusqu'à
     « toutes les saisons » — tout demander d'un coup tenait le pool de la
     base des secondes entières à chaque visite. */
  const plageSemaine = useMemo(() => plageDesDates((retro?.events ?? []).map((e) => e.day)), [retro])
  const caisseSemaine = useCaisseNuit('week_cards', plageSemaine)
  const caisseHistorique = useCaisseParMois('history')

  /* La règle vit dans un module pur, testée à part : ce qui est mis en avant
     plus haut ne se répète pas dans l'Historique — sauf recherche explicite. */
  const promues = useMemo(
    () => promotedEventIds({ current: data?.current ?? null, outlook, retrospective: retro }),
    [data?.current, outlook, retro],
  )
  const history = useMemo(
    () => projectHistory(data?.history ?? [], promues, recherche),
    [data, promues, recherche],
  )

  // Groupement par saison puis par mois (UX premium — jamais 300 cartes à plat).
  const grouped = useMemo(() => {
    const seasons = data?.seasons ?? []
    const bySeason = new Map<string, { season: Season | null; months: Map<string, SoireeView[]> }>()
    for (const s of history) {
      const d = s.starts_at.slice(0, 10)
      const season = seasons.find((x) => d >= x.start && d <= x.end) ?? null
      const key = season?.name ?? 'Hors saison'
      const month = new Date(s.starts_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' })
      const g = bySeason.get(key) ?? { season, months: new Map() }
      const arr = g.months.get(month) ?? []
      arr.push(s)
      g.months.set(month, arr)
      bySeason.set(key, g)
    }
    return [...bySeason.entries()]
  }, [history, data])

  /*
   * LES SECTIONS DE LA PAGE, NOMMÉES.
   *
   * Chacune rend EXACTEMENT ce qu'elle rendait avant : rien de leur contenu
   * n'a bougé. Ce qui change, c'est qu'elles ne sont plus écrites les unes
   * sous les autres dans le JSX — leur succession vient du défaut global de
   * la page, et se règle à l'écran.
   *
   * Une section qui n'a rien à montrer renvoie `null` et ne laisse aucune
   * trace : un titre suivi du vide serait pire que son absence.
   */
  const sections: Record<string, { label: string; render: () => ReactNode }> = {
    current: {
      label: 'Soirée en cours',
      render: () => (data?.current ? (
        <section aria-labelledby="so-en-cours">
          <h2 className="lc-section-title" id="so-en-cours">Soirée en cours</h2>
          <CurrentCard s={data.current} freshness={data.freshness} nuit={data.night} billetteries={data.billetteries} formules={data.current_formules} />
          <p className="lc-hint so-aide">{AIDE_SCANS}</p>
        </section>
      ) : null),
    },
    outlook: {
      label: 'Prochaines soirées',
      render: () => (outlook && outlook.length > 0 ? (
        <section aria-labelledby="so-outlook-h">
          <h2 className="lc-section-title" id="so-outlook-h">
            <TrendingUp size={16} aria-hidden="true" /> Prochaines soirées
          </h2>
          <div className="so-outlooks">
            {outlook.map((e) => <OutlookCard key={e.soiree.id} entry={e} onWhy={() => setWhyEntry(e)} />)}
          </div>
        </section>
      ) : null),
    },
    'last-weekend': {
      label: TITRE_RETRO[retro?.kind ?? 'LAST_WEEKEND'],
      render: () => (retro && retro.events.length > 0 ? <RetrospectiveHero retro={retro} caisse={caisseSemaine} /> : null),
    },
    market: { label: 'Autour de Rennes', render: () => <MarketBlock /> },
    history: {
      label: 'Historique',
      render: () => (
        <section aria-labelledby="so-historique">
          <div className="so-section-head so-section-head--wrap">
            <h2 className="lc-section-title" id="so-historique">Historique</h2>
            <div className="so-toolbar so-toolbar--inline">
              <Select compact value={period} options={PERIOD_OPTIONS} onChange={changePeriod} ariaLabel="Période affichée" />
              {period === 'custom' ? (
                <span className="so-daterange">
                  <label>Du <input type="date" className="lc-input so-date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} /></label>
                  <label>Au <input type="date" className="lc-input so-date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} /></label>
                </span>
              ) : null}
              <label className="so-search">
                <Search size={15} aria-hidden="true" />
                <input className="lc-input" type="search" placeholder="Rechercher"
                  value={recherche} onChange={(e) => setRecherche(e.target.value)}
                  aria-label="Rechercher une soirée par nom" />
              </label>
            </div>
          </div>

          <div className="so-sections" key={`${period}-${q}`}>
            {!data ? <p className="lc-hint">Chargement…</p>
              : grouped.length === 0 ? (
                <p className="gd-calm">{q ? 'Aucune soirée ne correspond.' : 'Aucune soirée sur cette période.'}</p>
              ) : grouped.map(([name, g]) => (
                <details key={name} className="so-season" open={grouped.length <= 2}>
                  <summary className="so-season__head">
                    <span className="so-season__name">Saison {name}</span>
                    <span className="lc-hint">
                      {g.season ? `${g.season.start.slice(0, 10)} → ${g.season.end.slice(0, 10)}` : ''}
                      {g.season?.boundary_source === 'FALLBACK' ? ' · bornes par défaut' : ''}
                      {g.season?.boundary_source === 'MANUAL' ? ' · bornes manuelles' : ''}
                      {' · '}{[...g.months.values()].reduce((a, x) => a + x.length, 0)} soirée(s)
                    </span>
                  </summary>
                  {[...g.months.entries()].map(([month, list]) => (
                    <MoisHistorique key={month} month={month} list={list} caisse={caisseHistorique} onChange={recharger} />
                  ))}
                </details>
              ))}
            {data && history.length < data.history_total && period !== 'all' ? (
              <p className="lc-hint">
                {data.history_total - history.length} soirée(s) hors période affichée — comparaisons et
                prévisions utilisent TOUJOURS l'historique complet.
              </p>
            ) : null}
          </div>
        </section>
      ),
    },
  }

  return (
    <Shell active="soirees" meta={meta}>
      <main className="lc-page lc-stagger hub--soirees so-page">
        <div className="so-section-head">
          <div>
            <h1 className="lc-title">Soirées</h1>
            <p className="lc-subtitle">Le pilotage des nuits du Live Club — présent, prévisions, historique.</p>
          </div>
          <div className="so-page__actions">
            {ordre.canEdit && !ordre.editing ? (
              <button type="button" className="lc-btn lc-btn--ghost" onClick={() => ordre.setEditing(true)}>
                <LayoutList size={15} aria-hidden="true" /> Modifier la page
              </button>
            ) : null}
            <Link href="/soirees/compare" className="lc-btn"><ArrowLeftRight size={15} aria-hidden="true" /> Comparer deux soirées</Link>
            <BoutonNouvelleSoiree etat={billetterieMaison} />
          </div>
        </div>

        {ordre.editing ? (
          <p className="so-reorder__notice">
            Glissez une section pour changer sa place. L’ordre obtenu deviendra
            celui de la page <strong>pour tout le monde</strong>, une fois
            enregistré en bas d’écran.
          </p>
        ) : null}

        {/* Gestion demande : un événement d'association n'a pas pu être rangé seul sous une soirée du club. */}
        <BandeauARattacher onChange={recharger} />

        {/* Billetterie Live Club · démo : fixe, HORS de la pile de sections (sa place ne se règle pas). */}
        <EncartBilletterieMaison etat={billetterieMaison} />
        <FenetreNouvelleSoiree etat={billetterieMaison} />

        {data && !data.freshness.configured ? (
          <section className="lc-panel gd-panel"><div className="lc-panel__body so-empty">
            <Ticket size={22} aria-hidden="true" />
            <div>
              <p className="so-empty__title">Billetterie non configurée</p>
              <p className="lc-hint">
                {permissions.permissions.includes('ticketing.configure')
                  ? <>Connectez-la dans <Link href="/settings/integrations">Paramètres → Intégrations</Link>.</>
                  : 'Un administrateur doit la connecter dans les paramètres.'}
              </p>
            </div>
          </div></section>
        ) : null}

        {/*
          L'ORDRE DES SECTIONS N'EST PLUS ÉCRIT ICI.

          Il vient du défaut global de la page (`page_layout_default`,
          page_key « soirees-list ») et se règle en mode édition. Chaque
          section reste rendue par le même code qu'avant ; seule leur
          succession est devenue une donnée.
        */}
        {ordre.order.map((sid) => {
          const noeud = sections[sid]
          if (!noeud) return null
          const contenu = noeud.render()
          // Hors édition, une section vide ne laisse rien. En édition on la
          // garde : régler la place d'un bloc les soirs où il est vide doit
          // rester possible.
          if (contenu === null && !ordre.editing) return null
          return (
            <ReorderableSection key={sid} state={ordre} id={sid} label={noeud.label}>
              {contenu ?? <p className="so-reorder__empty">Rien à afficher en ce moment.</p>}
            </ReorderableSection>
          )
        })}

        {data ? (
          <p className="lc-hint so-source">
            {(data.freshness_by_billetterie?.length ?? 0) > 1
              ? <>Billetteries : {data.freshness_by_billetterie!.map((f) => SOURCE_LABELS[f.cle] ?? f.billetterie).join(' · ')} · lecture seule</>
              : <>Source de billetterie : {SOURCE_LABELS[data.freshness.provider ?? ''] ?? '—'} · lecture seule</>}
          </p>
        ) : null}

        {/* ═══ drawer « Pourquoi cette prévision ? » ═══ */}
        <Drawer open={whyEntry !== null} title="Pourquoi cette prévision ?" onClose={() => setWhyEntry(null)}>
          {whyEntry ? (
            <>
              <p className="lc-hint" style={{ marginTop: 0 }}>
                {whyEntry.soiree.name} — fourchette estimée{' '}
                <strong>{whyEntry.forecast.range_low}–{whyEntry.forecast.range_high} scans</strong> ·
                confiance {whyEntry.forecast.confidence.toLowerCase()}. Méthode déterministe
                (comparables pondérés × rythme de préventes) — pas un intervalle statistique.
              </p>
              {(['POSITIF', 'NÉGATIF', 'NEUTRE'] as const).map((kind) => {
                const fs = whyEntry.forecast.factors.filter((f) => f.kind === kind)
                if (fs.length === 0) return null
                return (
                  <div key={kind} className="so-factors">
                    <h3 className={`so-factors__title so-factors__title--${kind.toLowerCase()}`}>
                      {kind === 'POSITIF' ? 'Facteurs positifs' : kind === 'NÉGATIF' ? 'Facteurs négatifs' : 'Contexte'}
                    </h3>
                    <ul>{fs.map((f) => <li key={f.label}><strong>{f.label}</strong> — {f.detail}</li>)}</ul>
                  </div>
                )
              })}
              {whyEntry.forecast.comparables.length > 0 ? (
                <div className="so-factors">
                  <h3 className="so-factors__title">
                    Références utilisées
                    {typeof whyEntry.forecast.family_refs_count === 'number' && whyEntry.forecast.family_refs_count > 0
                      ? ` : ${whyEntry.forecast.family_refs_count} soirée${whyEntry.forecast.family_refs_count > 1 ? 's' : ''} ${whyEntry.soiree.family ?? ''}`
                      : ''}
                  </h3>
                  {whyEntry.forecast.reference_quality === 'VERY_LOW' || whyEntry.forecast.reference_quality === 'LOW' ? (
                    <p className="lc-hint" style={{ margin: '0 0 6px' }}>Données comparables insuffisantes — fourchette large, confiance faible.</p>
                  ) : null}
                  <ul>
                    {whyEntry.forecast.comparables.map((c) => (
                      <li key={c.id}>
                        <Link href={`/soirees/${c.id}`} {...menuSoiree.soiree(c)}>{c.name}</Link> · {jourFr(c.starts_at)} · {c.final_scans} scans
                        {c.family_match === false ? ' · générique (même jour)' : ''}
                        {typeof c.weight === 'number' ? ` · poids ${c.weight}` : ''}
                        {c.atypical ? <span className="fam-atypique"> ATYPIQUE</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {(whyEntry.pressure?.warnings ?? []).length > 0 ? (
                <div className="so-factors">
                  <h3 className="so-factors__title">Événements contextuels</h3>
                  <ul>
                    {(whyEntry.pressure?.warnings ?? []).map((w, i) => (
                      <li key={i}>⚠ <strong>{w.venue_name}</strong> — {w.title}
                        {w.reasons.length > 0 ? ` (${w.reasons.join(', ')})` : ''} · impact potentiel {w.level === 'HIGH' ? 'élevé' : 'moyen'}
                        {' — audience pas forcément la nôtre, signalé sans peser mécaniquement sur la fourchette.'}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
        </Drawer>
      </main>
    </Shell>
  )
}
